/**
 * Inline SVG brand logos for the source picker.
 *
 * Each logo is a small (32×32) self-contained mark using the recognizable
 * brand color for its service. Kept inline because:
 *   - the wizard is rendered before network: no flash-of-unstyled-icon
 *   - no external URL means no hotlink fragility or third-party CSP woes
 *   - colors stay accurate across light/dark themes without extra assets
 *
 * Marks are stylized one- or two-letter initials over the brand color, not
 * pixel-perfect reproductions of the official logos — sufficient to make
 * each service unmistakable in the grid without raising trademark issues.
 */
import type { FC } from "react";

interface LogoProps {
  size?: number;
  className?: string;
}

function chip(bg: string, body: React.ReactNode, gradient?: string) {
  return (
    <>
      <rect width="32" height="32" rx="7" fill={gradient ? `url(#${gradient})` : bg} />
      {body}
    </>
  );
}

function Initial({
  text,
  fill = "#fff",
  fontSize = 13,
  y = 21,
}: {
  text: string;
  fill?: string;
  fontSize?: number;
  y?: number;
}) {
  return (
    <text
      x="16"
      y={y}
      textAnchor="middle"
      fontFamily="Inter, system-ui, sans-serif"
      fontSize={fontSize}
      fontWeight={700}
      fill={fill}
      letterSpacing="-0.5"
    >
      {text}
    </text>
  );
}

const SimulatedLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#1e1b4b", null)}
    <path
      d="M5 18 Q9 10 13 18 T21 18 T27 18"
      stroke="#a5b4fc"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

// ── Generic broker ──────────────────────────────────────────────────

const MQTTLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#660066", null)}
    {/* MQTT-style nested arcs evoking the wireless / pub-sub wave. */}
    <path
      d="M9 21 A6 6 0 0 1 21 21"
      stroke="#ec4899"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M6 21 A10 10 0 0 1 24 21"
      stroke="#f9a8d4"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    <circle cx="15" cy="21" r="1.6" fill="#fff" />
  </svg>
);

// ── Cloud IoT ───────────────────────────────────────────────────────

const AzureLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    <defs>
      <linearGradient id="azure-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0078d4" />
        <stop offset="100%" stopColor="#005ba1" />
      </linearGradient>
    </defs>
    {chip("#0078d4", null, "azure-g")}
    {/* Azure's "A" cloud wedge — two stylized triangles. */}
    <path d="M9 22 L15 9 L19 16 L13 16 Z" fill="#fff" opacity="0.95" />
    <path d="M16 22 L22 14 L26 22 Z" fill="#fff" opacity="0.85" />
  </svg>
);

const AwsLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#232f3e", null)}
    <text
      x="16"
      y="16"
      textAnchor="middle"
      fontFamily="Inter, system-ui, sans-serif"
      fontSize="9"
      fontWeight={800}
      fill="#fff"
      letterSpacing="0.5"
    >
      aws
    </text>
    {/* AWS smile arrow */}
    <path
      d="M6 22 Q16 27 26 22"
      stroke="#ff9900"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    <path d="M24 20 L26 22 L24 24" stroke="#ff9900" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GoogleLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#fff", null)}
    {/* Multi-color "G". */}
    <path d="M16 6 A10 10 0 1 0 25.5 19 H16 V15 H22 A6 6 0 1 1 16 10 A6 6 0 0 1 19.5 11 L22.5 8.5 A10 10 0 0 0 16 6" fill="#4285F4" />
    <path d="M16 6 A10 10 0 0 0 7 11.5 L10.5 14 A6 6 0 0 1 16 10 A6 6 0 0 1 19.5 11 L22.5 8.5 A10 10 0 0 0 16 6" fill="#EA4335" />
    <path d="M7 11.5 A10 10 0 0 0 7 20.5 L10.5 18 A6 6 0 0 1 10.5 14 Z" fill="#FBBC05" />
    <path d="M16 26 A10 10 0 0 0 25.5 19 H16 V15 H22 A6 6 0 0 1 16 22 A6 6 0 0 1 10.5 18 L7 20.5 A10 10 0 0 0 16 26" fill="#34A853" />
  </svg>
);

// ── Industrial IoT ─────────────────────────────────────────────────

const SiemensLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#009999", null)}
    <Initial text="MS" />
    <circle cx="6" cy="6" r="1.5" fill="#fff" opacity="0.6" />
    <circle cx="26" cy="26" r="1.5" fill="#fff" opacity="0.6" />
  </svg>
);

const GePredixLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#005eb8", null)}
    <circle cx="16" cy="16" r="9" fill="none" stroke="#fff" strokeWidth="1.5" />
    <Initial text="GE" fontSize={11} y={20} />
  </svg>
);

const PtcLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#0c2340", null)}
    <Initial text="PTC" fontSize={10} y={20} />
    <path d="M5 5 L11 5 L8 11 Z" fill="#ec1c2e" />
  </svg>
);

// ── Industrial protocols ───────────────────────────────────────────

const OpcUaLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#0a2540", null)}
    <circle cx="16" cy="14" r="6" fill="none" stroke="#41baeb" strokeWidth="2" />
    <Initial text="UA" fontSize={9} y={27} fill="#41baeb" />
  </svg>
);

const ModbusLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#1f2937", null)}
    {/* RS-485 connector pins */}
    <rect x="6" y="13" width="3" height="6" rx="1" fill="#22d3ee" />
    <rect x="11" y="13" width="3" height="6" rx="1" fill="#22d3ee" />
    <rect x="16" y="13" width="3" height="6" rx="1" fill="#22d3ee" />
    <rect x="21" y="13" width="3" height="6" rx="1" fill="#22d3ee" />
    <Initial text="MOD" fontSize={7} y={28} fill="#22d3ee" />
  </svg>
);

const BacnetLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#4338ca", null)}
    {/* Building outline */}
    <path d="M9 22 V13 H16 V9 H23 V22 Z" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
    <rect x="11" y="15" width="2" height="2" fill="#fff" />
    <rect x="18" y="12" width="2" height="2" fill="#fff" />
    <rect x="18" y="17" width="2" height="2" fill="#fff" />
  </svg>
);

const RestLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#0f172a", null)}
    {/* Webhook-style curl mark */}
    <path
      d="M11 14 a4 4 0 1 1 4 6 L11 25"
      stroke="#22d3ee"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M21 18 a4 4 0 1 0 -6 4 L17 26"
      stroke="#a78bfa"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

// ── Time-series databases ──────────────────────────────────────────

const InfluxLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    <defs>
      <linearGradient id="influx-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#22adf6" />
        <stop offset="100%" stopColor="#9b2fae" />
      </linearGradient>
    </defs>
    {chip("#22adf6", null, "influx-g")}
    {/* Stylized data hill */}
    <path d="M6 22 L13 12 L18 17 L26 8" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="13" cy="12" r="1.6" fill="#fff" />
    <circle cx="18" cy="17" r="1.6" fill="#fff" />
    <circle cx="26" cy="8" r="1.6" fill="#fff" />
  </svg>
);

const TimescaleLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#fdb515", null)}
    {/* Clock face on cylinder */}
    <ellipse cx="16" cy="11" rx="9" ry="3" fill="#1f2937" />
    <path d="M7 11 V21 A9 3 0 0 0 25 21 V11" fill="#1f2937" />
    <circle cx="16" cy="16" r="4" fill="#fdb515" />
    <path d="M16 13 V16 L18 17" stroke="#1f2937" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PrometheusLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#1f2937", null)}
    {/* Prometheus flame */}
    <path
      d="M16 6 Q12 12 14 16 Q16 13 16 10 Q19 14 17 19 Q20 17 21 13 Q23 19 22 22 Q25 19 23 14 Q22 8 16 6 Z"
      fill="#e6522c"
    />
    <rect x="10" y="22" width="12" height="2" rx="1" fill="#e6522c" />
  </svg>
);

const VictoriaLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#621773", null)}
    {/* "V" mark */}
    <path d="M7 9 L16 24 L25 9" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="16" cy="24" r="2" fill="#ec4899" />
  </svg>
);

const QuestLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#d14671", null)}
    {/* Stylized "Q" */}
    <circle cx="16" cy="15" r="7" fill="none" stroke="#fff" strokeWidth="2.2" />
    <path d="M19 18 L24 25" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const TimestreamLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    {chip("#232f3e", null)}
    <circle cx="16" cy="16" r="7.5" fill="none" stroke="#ff9900" strokeWidth="2" />
    <path d="M16 11 V16 L19 18" stroke="#ff9900" strokeWidth="2" strokeLinecap="round" />
    <Initial text="TS" fontSize={6} y={29} fill="#ff9900" />
  </svg>
);

const KdbLogo: FC<LogoProps> = ({ size = 32, className }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className}>
    <defs>
      <linearGradient id="kdb-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ef4444" />
        <stop offset="100%" stopColor="#a855f7" />
      </linearGradient>
    </defs>
    {chip("#ef4444", null, "kdb-g")}
    <Initial text="kx" fontSize={14} y={22} />
  </svg>
);

/** Registry keyed by the backend's source `key`. CreateDeviceWizard looks
 *  up the logo here; missing entries fall back to a Lucide icon. */
export const SOURCE_LOGOS: Record<string, FC<LogoProps>> = {
  simulated: SimulatedLogo,
  mqtt: MQTTLogo,
  azure_iot_hub: AzureLogo,
  aws_iot_core: AwsLogo,
  gcp_iot_core: GoogleLogo,
  siemens_mindsphere: SiemensLogo,
  ge_predix: GePredixLogo,
  ptc_thingworx: PtcLogo,
  opcua: OpcUaLogo,
  modbus_tcp: ModbusLogo,
  bacnet: BacnetLogo,
  rest_webhook: RestLogo,
  influxdb: InfluxLogo,
  timescaledb: TimescaleLogo,
  prometheus: PrometheusLogo,
  victoriametrics: VictoriaLogo,
  questdb: QuestLogo,
  timestream: TimestreamLogo,
  kdb: KdbLogo,
};

export type SourceLogoComponent = FC<LogoProps>;
